/**
 * useSuggestions — type-ahead suggestions hook.
 *
 * React binding over the framework-free core in `searchCore/suggestions`,
 * which owns debouncing, the LRU cache and request cancellation and is also
 * used by the embeddable widget.
 */

import { useEffect, useState } from 'react';
import { apiService } from '../services/apiService';
import {
  DEFAULT_DEBOUNCE_MS,
  DEFAULT_MIN_LENGTH,
  DEFAULT_SIZE,
  requestSuggestions,
  type SuggestFn,
} from '../searchCore/suggestions';
import type { SuggestionItem } from '../types/search';

interface UseSuggestionsOptions {
  /** Minimum trimmed query length before any fetch. Default 2. */
  minLength?: number;
  /** Debounce in ms. Default 120 (snappier than typical 250 ms autocomplete). */
  debounceMs?: number;
  /** Number of suggestions to request from the backend. Default 8. */
  size?: number;
  /** Optional user-preferred language (passed to the backend filter). */
  userLanguage?: string;
  /** Optional audience filter (e.g. 'search_frontend'). */
  audience?: string;
}

interface UseSuggestionsResult {
  suggestions: SuggestionItem[];
  isLoading: boolean;
  error: Error | null;
}

const suggest: SuggestFn = (params, signal) =>
  apiService.suggest(params, signal).then((res) => res.suggestions);

export function useSuggestions(
  query: string,
  options: UseSuggestionsOptions = {}
): UseSuggestionsResult {
  const {
    minLength = DEFAULT_MIN_LENGTH,
    debounceMs = DEFAULT_DEBOUNCE_MS,
    size = DEFAULT_SIZE,
    userLanguage,
    audience,
  } = options;

  const [suggestions, setSuggestions] = useState<SuggestionItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    return requestSuggestions(
      query,
      { suggest, minLength, debounceMs, size, userLanguage, audience },
      {
        onLoading: setIsLoading,
        onData: (items) => {
          setSuggestions(items);
          setError(null);
        },
        onError: setError,
      }
    );
  }, [query, minLength, debounceMs, size, userLanguage, audience]);

  return { suggestions, isLoading, error };
}
